<template>
    <div
        class="ds-notif ds-notification"
        :class="{
            [`variation-${variation}`]: true,
            [`ds-notif-${type}`]: true,
            'ds-notif-no-icon': !$slots.icon && !icon,
        }"
        @mouseover="onMouseOver"
        @mouseout="onMouseOut"
    >
        <div v-if="icon || $slots.icon" class="ds-notif-icon">
            <slot name="icon">
                <DSIcon
                    v-if="icon"
                    :code="`${variation === 2 ? 'default-line' : 'default-fill'}/${icon}`"
                    :size="variation === 1 ? 'xxl' : 'xl'"
                />
            </slot>
        </div>
        <div class="ds-notif-content">
            <slot>
                <span v-if="message" v-html="message" />
            </slot>
        </div>
        <div v-if="action || $slots.action" class="ds-notif-action">
            <slot name="action">
                <span v-if="action" v-html="action" />
            </slot>
        </div>
        <button
            v-if="dismissable"
            class="ds-notif-dismiss"
            aria-label="Close notification"
            @click="dismiss"
        >
            <slot name="dismiss">
                <DSIcon code="default-line/close" />
            </slot>
        </button>
    </div>
</template>
<script setup>
import { ref, onMounted } from '#imports';

const props = defineProps({
    type: {
        type: String,
        default: 'information',
        validator: type => [
            'success',
            'information',
            'note',
            'warning',
            'error',
        ].includes(type),
    },
    variation: {
        type: [String, Number],
        default: 1,
        validator: v => [1, 2].includes(Number(v)),
    },
    dismissable: {
        type: Boolean,
        default: props => props.variation === 1,
    },
    iconMap: {
        type: Object,
        default: () => ({
            success: 'info',
            information: 'info',
            note: 'info',
            warning: 'info',
            error: 'error',
        }),
    },
    icon: {
        type: [String, Boolean],
        default: props => props.iconMap[props.type],
    },
    message: {
        type: String,
        default: undefined,
    },
    action: {
        type: String,
        default: '',
    },
    transitionDelay: {
        type: Number,
        default: 500,
    },
    dismissDelay: {
        type: Number,
        default: 20000,
    },
    autoDismissable: {
        type: Boolean,
        default: true,
    },
    container: {
        type: [Boolean, Object],
        default: false,
    },
    pauseOnHover: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['dismiss']);

const dismissTimer = ref(null);

const dismiss = event => emit('dismiss', event);

const autoDismiss = () => {
    clearTimeout(dismissTimer.value);
    dismissTimer.value = setTimeout(() => dismiss(), props.dismissDelay);
};

onMounted(() => {
    if (props.dismissable && props.autoDismissable) autoDismiss();
});

const onMouseOver = _e => {
    if (props.pauseOnHover) clearTimeout(dismissTimer.value);
};

const onMouseOut = _e => {
    if (props.pauseOnHover) autoDismiss();
};
</script>

<style scoped>
.ds-notif {
  display: grid;
  align-items: center;
  margin-block: 0.25rem 1.5rem;
  position: relative;
  background-color: var(--color-base-white);
  grid-template-columns: auto 1fr auto auto;
}
.ds-notif.ds-notif-no-icon {
  grid-template-columns: 1fr auto auto;
}
@media (min-width: 480px) {
  .ds-notif.ds-notif-no-icon > *:not(.ds-notif-icon),
  .ds-notif > *:not(:last-of-type) {
    margin-right: 1rem;
  }
}
.ds-notif.variation-1 {
  box-shadow: 0 0.25rem 0.5rem 0 var(--color-base-black-mine-shaft-opaque);
  padding: 1.25rem 1.5rem 1.25rem 1rem;
}
.ds-notif.variation-1 .ds-notif-content {
  font-weight: 700;
  font-size: 1.125rem;
}
.ds-notif.variation-2 {
  border-radius: 0.25rem;
  padding: 0.75rem 1rem;
}
.ds-notif.variation-2 .ds-notif-icon {
  margin-right: 0.5rem;
}
.ds-notif.variation-2 .ds-notif-content {
  font-size: 1rem;
}
.ds-notif::before {
  content: "";
  display: block;
  position: absolute;
  width: 100%;
  height: 0.25rem;
  top: -0.25rem;
  left: 0;
  border-top-right-radius: 0.25rem;
  border-top-left-radius: 0.25rem;
  opacity: 0;
  transform: scaleX(0);
  transform-origin: center left;
  animation: border 0.3s ease-in-out forwards;
  animation-delay: 0.15s;
}
.ds-notif :deep() [class*=bg] {
  stroke-width: 2px;
}
.ds-notif :deep() .icon {
  fill: var(--color-base-white-default);
}
.ds-notif.ds-notif-success {
  background-color: var(--color-success-100);
}
.ds-notif.ds-notif-success .ds-notif-icon {
  color: var(--color-success-400);
}
.ds-notif.ds-notif-success.variation-1::before {
  background-color: var(--color-success-400);
}
.ds-notif.ds-notif-success.variation-1 :deep([class*="bg"]) {
  stroke: var(--color-success-500);
}
.ds-notif.ds-notif-success.variation-2 {
  border: 1px solid var(--color-success-400);
}
.ds-notif.ds-notif-success :deep([class*="bg"]) {
  fill: var(--color-success-400);
}
.ds-notif.ds-notif-information {
  background-color: var(--color-information-100);
}
.ds-notif.ds-notif-information .ds-notif-icon {
  color: var(--color-information-400);
}
.ds-notif.ds-notif-information.variation-1::before {
  background-color: var(--color-information-400);
}
.ds-notif.ds-notif-information.variation-1 :deep([class*="bg"]) {
  stroke: var(--color-information-500);
}
.ds-notif.ds-notif-information.variation-2 {
  border: 1px solid var(--color-information-400);
}
.ds-notif.ds-notif-information :deep([class*="bg"]) {
  fill: var(--color-information-400);
}
.ds-notif.ds-notif-error {
  background-color: var(--color-error-100);
}
.ds-notif.ds-notif-error .ds-notif-icon {
  color: var(--color-error-400);
}
.ds-notif.ds-notif-error.variation-1::before {
  background-color: var(--color-error-400);
}
.ds-notif.ds-notif-error.variation-1 :deep([class*="bg"]) {
  stroke: var(--color-error-500);
}
.ds-notif.ds-notif-error.variation-2 {
  border: 1px solid var(--color-error-400);
}
.ds-notif.ds-notif-error :deep([class*="bg"]) {
  fill: var(--color-error-400);
}
.ds-notif.ds-notif-warning {
  background-color: var(--color-warning-100);
}
.ds-notif.ds-notif-warning .ds-notif-icon {
  color: var(--color-warning-400);
}
.ds-notif.ds-notif-warning.variation-1::before {
  background-color: var(--color-warning-400);
}
.ds-notif.ds-notif-warning.variation-1 :deep([class*="bg"]) {
  fill: var(--color-warning-400);
  stroke: var(--color-warning-500);
}
.ds-notif.ds-notif-warning.variation-1 :deep(.icon) {
  fill: var(--color-base-black-default);
}
.ds-notif.ds-notif-warning.variation-2 {
  border: 1px solid var(--color-warning-500);
}
.ds-notif.ds-notif-warning.variation-2 :deep([class*="bg"]) {
  fill: var(--color-primary-800);
}
.ds-notif.ds-notif-note {
  background-color: var(--color-base-white-default);
}
.ds-notif.ds-notif-note .ds-notif-icon {
  color: var(--color-warning-400);
}
.ds-notif.ds-notif-note.variation-1 :deep([class*="bg"]) {
  fill: var(--color-warning-400);
  stroke: var(--color-warning-500);
}
.ds-notif.ds-notif-note.variation-1 :deep(.icon) {
  fill: var(--color-base-black-default);
}
.ds-notif.ds-notif-note.variation-2 {
  border: 1px solid var(--color-base-grey-200);
}
.ds-notif.ds-notif-note.variation-2 :deep([class*="bg"]) {
  fill: var(--color-primary-700);
}
.ds-notif .ds-notif-icon {
  display: grid;
}
.ds-notif .ds-notif-content {
  line-height: 1.5rem;
}
.ds-notif .ds-notif-action {
  display: inline-flex;
  align-self: center;
}
.ds-notif .ds-notif-dismiss {
  display: grid;
  padding: 0;
  margin-left: 1rem;
}
@media (max-width: 767px) {
  .ds-notif {
    display: grid;
    column-gap: 0;
    row-gap: 1.5rem;
    align-items: start;
    grid-template-columns: 2.75rem 1fr auto;
  }
  .ds-notif.ds-notif-no-icon {
    grid-template-columns: 1fr auto;
  }
  .ds-notif .ds-notif-dismiss {
    margin-left: 0.25rem;
  }
  .ds-notif .ds-notif-action {
    justify-self: end;
    grid-row: 2;
    grid-column: 1/-1;
  }
}

@keyframes border {
  to {
    opacity: 1;
    transform: scaleX(1);
  }
}
.out-in-enter-active,
.out-in-leave-to {
  opacity: 0;
  transition: opacity 0.3s linear;
}
</style>
